import { Typography } from '@mui/material';
import type { ExposureOrGroup } from 'api/submission';
import { ExposuresList, ExposuresListItem, ExposuresWrapper } from 'components/ExposuresWrapper';
import { formatDate } from 'helpers/utils';
import { useExposuresOrGroupFields } from 'hooks/useExposuresOrGroupFields';
import { type FC, useRef } from 'react';
import type { IDriver } from '../Drivers/Drives.types';
import type { IDrivingIncident } from './DrivingIncidents.types';
import { DrivingIncidentsModal } from './components/Modal';
import type { DrivingIncidentsModalRef } from './components/Modal/DrivingIncidentsModal.types';

const category = 'driving incident';

const renderIncidents = (indicident: ExposureOrGroup<IDrivingIncident>) => {
  const selectedIncidents = indicident.data.pol_incidents_type.split(';');

  return selectedIncidents.length > 1 ? 'Multiple' : selectedIncidents[0];
};

const DrivingIncidents: FC = () => {
  const { relatedResource, performAction } = useExposuresOrGroupFields<IDrivingIncident>({
    type: 'group',
    name: 'pol_incidents',
  });

  const { relatedResource: drivers } = useExposuresOrGroupFields<IDriver>({
    type: 'group',
    name: 'pol_drivers',
  });

  const relatedDriver = (data: IDrivingIncident) =>
    drivers?.find(
      (driver) => driver.data.pol_drivers_id === data.pol_incidents_driver.split(';')[0],
    );

  const title = (data: IDrivingIncident) => {
    const driver = relatedDriver(data);

    return `${driver?.data.pol_drivers_first} ${driver?.data.pol_drivers_last}`;
  };

  const drivingIncidentsModalRef = useRef<DrivingIncidentsModalRef>(undefined);

  const handleAdd = async (data: IDrivingIncident) =>
    performAction({
      action: 'add',
      data,
      loadingText: `Adding ${title(data)}`,
    });

  const handleUpdate = async (data: IDrivingIncident, id: string) =>
    performAction({
      action: 'update',
      data,
      resourceId: id,
      loadingText: `Updating the ${title(data)}`,
    });

  const handleDelete = async (incident: ExposureOrGroup<IDrivingIncident>) =>
    performAction({
      action: 'delete',
      resourceId: incident.id,
      loadingText: `Deleting the ${title(incident.data)}`,
    });

  return (
    <>
      <DrivingIncidentsModal
        ref={drivingIncidentsModalRef}
        handleAdd={handleAdd}
        handleUpdate={handleUpdate}
        category={category}
      />

      <ExposuresWrapper
        title="Add driving incidents"
        addButtonText={`Add ${category}`}
        onAddBtnClick={() => drivingIncidentsModalRef.current?.openModal()}
      >
        <ExposuresList>
          {relatedResource.map((incident) => (
            <ExposuresListItem
              key={incident.id}
              category={category}
              title={title(incident.data)}
              onEdit={() => drivingIncidentsModalRef.current?.openModal(incident)}
              onDelete={() => {
                handleDelete(incident);
              }}
            >
              <Typography>{formatDate(incident.data.pol_incidents_date)}</Typography>

              <Typography>{renderIncidents(incident)}</Typography>
            </ExposuresListItem>
          ))}
        </ExposuresList>
      </ExposuresWrapper>
    </>
  );
};

export default DrivingIncidents;
